/**
 * ConfirmRenderer — yes/no confirmation pair. The user sees a pair of
 * labelled buttons; the chosen value posts back as
 * `structured_value: {confirmed: true|false}`.
 *
 * History: shows an inert "✓ Confirmed" / "✗ Cancelled" pill based on
 * `ctx.answeredWith`.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ConfirmContentItem } from '@wg/types';
import type { Renderer } from '../registry';

const ConfirmRenderer: Renderer = ({ item, ctx }) => {
  const { t } = useTranslation();
  const it = item as ConfirmContentItem;
  const inert = ctx.isHistory;
  const confirmLabel = it.confirm_label || t('common.confirm');
  const cancelLabel = it.cancel_label || t('common.cancel');

  if (inert) {
    const a = ctx.answeredWith as { confirmed?: boolean } | undefined;
    const confirmed =
      a && typeof a.confirmed === 'boolean' ? a.confirmed : null;
    return (
      <div className="wg-chat__confirm is-history">
        <p className="wg-chat__confirm-title">{it.title}</p>
        {confirmed === true && (
          <span className="wg-chat__chip is-chosen wg-chat__chip--primary">
            ✓ {confirmLabel}
          </span>
        )}
        {confirmed === false && (
          <span className="wg-chat__chip is-chosen">✗ {cancelLabel}</span>
        )}
        {confirmed === null && (
          <span className="wg-chat__chip is-muted">no answer</span>
        )}
      </div>
    );
  }

  const send = (confirmed: boolean) => {
    ctx.onSendMessage(confirmed ? confirmLabel : cancelLabel, {
      in_response_to: it.prompt_id,
      structured_value: { confirmed },
    });
  };

  return (
    <div className="wg-chat__confirm" role="group" aria-label={it.title}>
      <p className="wg-chat__confirm-title">{it.title}</p>
      {it.body && <p className="wg-chat__confirm-body">{it.body}</p>}
      <div className="wg-chat__confirm-actions">
        <button
          type="button"
          className="wg-chat__chip"
          onClick={() => send(false)}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`wg-chat__chip ${
            it.destructive
              ? 'wg-chat__chip--destructive'
              : 'wg-chat__chip--primary'
          }`}
          onClick={() => send(true)}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
};

export default ConfirmRenderer;
